import React, { Component } from "react";
import { connect } from "react-redux";
import { check } from "@xams-utils/check-types";

import { getGuid } from "redux/reducers/exam/selectors";
import { getExamData, getSchedulesData, getSessionData } from "redux/reducers/selectors";
import { getScheduleDataByExamGuid } from "redux/reducers/schedules/selectors";
import { getUserSessionData } from "redux/reducers/session/selectors";
import { getUserGuid } from "redux/reducers/session/user/selectors";
import {
    getProctorProvider,
    getObjectId,
    getQualificationId,
} from "redux/reducers/schedules/schedule/selectors";

import { PROCTORING_TYPES } from "constants/proctoring";
import { assessmentApi } from "libs/api/interface/api-assessment";

import { Align } from "components/layout/align";
import { MaterialText } from "components/presentation/material-text";
import { AppPageModifier } from "components/pages/app-page-modifier";
import { LoadingSpinner } from "components/presentation/loading-spinner";

// TODO: replace with the production OctoProctor host once confirmed - supervisor.js
// must always be loaded from the same host as the "url" passed to the Supervisor instance.
const OCTO_PROCTOR_URL = "https://openawards.proctoring.app";

class OpenOctoProctor extends Component {
    state = { error: null };

    constructor(props) {
        super(props);

        this.handleStart = this.handleStart.bind(this);
        this.handleFail = this.handleFail.bind(this);
        this.handleScriptLoaded = this.handleScriptLoaded.bind(this);
    }

    componentDidMount() {
        if (this.isOctoProctor()) {
            this.addSupervisorScript();
        } else {
            const { onSuccess } = this.props;
            console.log("Not OctoProctor");
            onSuccess();
        }
    }

    componentWillUnmount() {
        if (this.script) {
            this.script.removeEventListener("load", this.handleScriptLoaded);
            this.script.removeEventListener("error", this.handleScriptLoaded);
        }
    }

    isOctoProctor() {
        const { proctoringProvider } = this.props;

        return proctoringProvider === PROCTORING_TYPES.OCTO_PROCTOR;
    }

    addSupervisorScript() {
        const error = this.checkParameters();
        if (check.nonEmptyObject(error)) {
            this.setState({ error });
            return;
        }

        this.script = document.createElement("script");
        this.script.type = "application/javascript";
        this.script.src = `${OCTO_PROCTOR_URL}/sdk/supervisor.js`;
        this.script.async = true;

        document.body.appendChild(this.script);

        this.script.addEventListener("load", this.handleScriptLoaded);
        this.script.addEventListener("error", this.handleScriptLoaded);
    }

    checkParameters() {
        const { userGuid, qualificationId, objectId } = this.props;

        if (!check.nonEmptyString(userGuid)) {
            return { text: "User GUID is blank" };
        } else if (
            !check.integer(qualificationId) &&
            !check.nonEmptyString(qualificationId)
        ) {
            return { text: "qualification ID (course ID) is blank" };
        } else if (
            !check.integer(objectId) &&
            !check.nonEmptyString(objectId)
        ) {
            return { text: "activity ID (object ID) is blank" };
        }

        return null;
    }

    handleScriptLoaded(e) {
        console.log("handleScriptLoaded", e);
        if (e.type !== "load") {
            console.log("OctoProctor Script Error", e);
            this.setState({ error: e });
            return;
        }

        this.startSession();
    }

    startSession() {
        const { userGuid, qualificationId, objectId } = this.props;

        const supervisor = new window.Supervisor({ url: OCTO_PROCTOR_URL });

        // stored globally so the exam-outro "close" step can call supervisor.stop()
        window.supervisor = supervisor;

        supervisor.on("start", this.handleStart);
        supervisor.on("fail", this.handleFail);

        const token = assessmentApi
            .getOctoProctorToken(userGuid, qualificationId, objectId)
            .then((response) => JSON.parse(response).token);

        supervisor.start({ provider: "jwt", token });
    }

    handleStart() {
        const { onSuccess } = this.props;
        console.log("OctoProctor session started");
        onSuccess();
    }

    handleFail() {
        console.log("OctoProctor session failed to start");
        this.setState({ error: { text: "Unable to start the proctoring session" } });
    }

    displayError(_error) {
        const error = check.nonEmptyObject(_error)
            ? _error
            : { text: "Error loading script" };

        const a = [];
        for (const [key, value] of Object.entries(error)) {
            a.push([key, value]);
        }

        return (
            <div>
                {a.map((item) => (
                    <div>
                        <MaterialText>
                            <b>{item[0]}</b>: {item[1]}
                        </MaterialText>
                    </div>
                ))}
            </div>
        );
    }

    render() {
        if (!this.isOctoProctor()) {
            return null;
        }

        const { error } = this.state;
        const title = "OctoProctor";

        if (error) {
            return (
                <React.Fragment>
                    <AppPageModifier
                        id="octo-proctor-error"
                        appBarProps={{
                            loading: false,
                            title: `${title} Error`,
                            logout: true,
                        }}
                    />
                    <Align top>
                        <div style={{ paddingTop: "20px" }}>
                            <div>
                                <MaterialText variant="h6">
                                    {title} Error
                                </MaterialText>
                            </div>
                            <div>{this.displayError(error)}</div>
                        </div>
                    </Align>
                </React.Fragment>
            );
        }

        return (
            <React.Fragment>
                <AppPageModifier
                    id="octo-proctor-loading"
                    appBarProps={{
                        loading: true,
                        title: `Starting ${title}`,
                        logout: false,
                        loadingTitle: true,
                    }}
                />
                <LoadingSpinner size="lg" />;
            </React.Fragment>
        );
    }
}

const mapStoreToProps = (store) => {
    const examData = getExamData(store);
    const examGuid = getGuid(examData);

    const schedulesData = getSchedulesData(store);
    const scheduleData = getScheduleDataByExamGuid(schedulesData)(examGuid);
    const proctoringProvider = getProctorProvider(scheduleData);
    const objectId = getObjectId(scheduleData);
    const qualificationId = getQualificationId(scheduleData);

    const userGuid = getUserGuid(getUserSessionData(getSessionData(store)));

    return {
        proctoringProvider,
        userGuid,
        objectId,
        qualificationId,
    };
};

OpenOctoProctor = connect(mapStoreToProps)(OpenOctoProctor);

export { OpenOctoProctor };
